import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { formatDateTimeStr } from '@/utils/renderUtils';
import { fetchDictOptions } from '@/redux/components/dictOptionsSlice';
import { babyMonitorDevices, readBabyMonitorView } from './BabyMonitor';

const levelColors = {
  error: '#ff6b6b',
  warning: '#fbbf24',
  info: '#00e5ff',
  success: '#00d4aa'
};

function AlertList({ title, storeId }) {
  const dispatch = useDispatch();
  const sse = useSelector((state) => state.sse);
  const yingshiDeviceList = useSelector((state) => state.yingshi.deviceList);
  const dictOptions = useSelector((state) => state.components.dictOptions);

  const selectedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';

  useEffect(() => {
    dispatch(fetchDictOptions({ typeCode: 'nursing_type' }));
  }, [dispatch]);

  const items = useMemo(() => {
    const nursingList = Array.isArray(dictOptions?.nursing_type?.data)
      ? dictOptions.nursing_type.data
      : [];
    const nursingLabelByValue = new Map();
    for (let i = 0; i < nursingList.length; i += 1) {
      const item = nursingList[i];
      if (!item || item.value == null || item.value === '') continue;
      const raw = item.label != null ? item.label : (item.desc != null ? item.desc : item.value);
      nursingLabelByValue.set(String(item.value), String(raw == null ? '' : raw).trim());
    }
    const views = babyMonitorDevices(yingshiDeviceList, selectedStoreId)
      .map((device) => readBabyMonitorView(device, sse, nursingLabelByValue))
      .filter((view) => view.warning);
    views.sort((a, b) => (b.eventTimeMs || 0) - (a.eventTimeMs || 0));
    return views;
  }, [yingshiDeviceList, selectedStoreId, sse, dictOptions]);

  const scrollRef = useRef(null);
  const posRef = useRef(0);
  const rafRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [openKey, setOpenKey] = useState('');

  useEffect(() => {
    posRef.current = 0;
    if (scrollRef.current) {
      scrollRef.current.style.transform = 'translateY(0px)';
    }
  }, [items]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || items.length === 0) return undefined;
    const halfHeight = el.scrollHeight / 2;
    const speed = 0.2;
    const animate = () => {
      if (!paused && !openKey && halfHeight > 0) {
        posRef.current += speed;
        if (posRef.current >= halfHeight) {
          posRef.current -= halfHeight;
        }
        el.style.transform = `translateY(-${posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused, openKey, items]);

  const renderAlert = (view, suffix) => {
    const levelClass = view.hasBaby === false ? 'warning' : 'error';
    const summary = view.statusTail || '--';
    const bedText = view.bedNo ? `${view.bedNo}号床` : '--';
    return (
      <div
        key={`${view.key}-${suffix}`}
        className={`alert-item ${levelClass}`}
        onClick={() => setOpenKey(view.key)}
      >
        <span className="alert-level" style={{ background: levelColors[levelClass] }}></span>
        <div className="alert-content">
          <span className="alert-message">{summary}</span>
          <div className="alert-meta">
            <span className="alert-location">{bedText}</span>
            <span className="alert-time">{view.eventTimeMs > 0 ? formatDateTimeStr(view.eventTimeMs) : '--'}</span>
          </div>
        </div>
      </div>
    );
  };

  const openItem = items.find((view) => view.key === openKey) || null;
  const openSerial = openItem ? openItem.deviceSerial : '';
  const openChannel = openItem ? openItem.channelNo : '';
  const openBed = openItem ? openItem.bedNo : '';
  const openImage = openItem ? openItem.screenUrl : '';
  const openSummary = openItem ? (openItem.statusTail || '--') : '';
  const openTime = openItem ? openItem.eventTimeMs : 0;

  return (
    <div className="alert-panel">
      <div className="alert-header">
        <span className="alert-title">{title}</span>
        <div className="alert-badge">
          <span className="badge-dot"></span>
          <span>实时</span>
        </div>
      </div>
      <div
        className="alert-body"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {items.length === 0 ? (
          <div className="alert-item info">
            <span className="alert-level" style={{ background: levelColors.info }}></span>
            <div className="alert-content">
              <span className="alert-message">暂无婴儿告警</span>
            </div>
          </div>
        ) : (
          <div className="alert-scroll" ref={scrollRef}>
            {items.map((item, i) => renderAlert(item, i))}
            {items.map((item, i) => renderAlert(item, `dup-${i}`))}
          </div>
        )}
      </div>
      {openItem && createPortal(
        <div className="alert-detail-overlay" onClick={() => setOpenKey('')}>
          <div className="alert-detail-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="ad-close" onClick={() => setOpenKey('')}>×</button>
            <div className="ad-cats">
              <button type="button" className="ad-cat active">
                <span className="ad-cat-name">全部</span>
                <span className="ad-cat-count">{items.length}</span>
              </button>
            </div>
            <div className="ad-cols">
              <aside className="ad-side">
                <div className="ad-side-title">
                  <span className="ad-side-dot"></span>
                  <span>实时告警</span>
                  <span className="ad-side-count">{items.length}</span>
                </div>
                <div className="ad-side-body">
                  <div className="ad-side-scroll">
                    {items.map((view) => {
                      const levelClass = view.hasBaby === false ? 'warning' : 'error';
                      return (
                        <div
                          key={view.key}
                          className={`ad-side-item ${levelClass}${view.key === openKey ? ' active' : ''}`}
                          onClick={() => setOpenKey(view.key)}
                        >
                          <span className="ad-side-level" style={{ background: levelColors[levelClass] }}></span>
                          <div className="ad-side-content">
                            <span className="ad-side-msg">{view.statusTail || '--'}</span>
                            <span className="ad-side-time">{view.eventTimeMs > 0 ? formatDateTimeStr(view.eventTimeMs) : '--'}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </aside>
              <div className="ad-main">
                <div className="ad-top">
                  <div className="ad-top-left">
                    <span className="ad-warn-icon">⚠</span>
                    <span className="ad-status">{openBed ? `${openBed}号床` : '婴儿告警'}</span>
                  </div>
                  <span className="ad-message">{openSummary}</span>
                </div>
                <div className="ad-info-row">
                  <div className="ad-info-item">
                    <span className="ad-info-label">发出</span>
                    <span className="ad-info-value">{openTime > 0 ? formatDateTimeStr(openTime) : '--'}</span>
                  </div>
                  <div className="ad-info-item">
                    <span className="ad-info-label">设备</span>
                    <span className="ad-info-value">{openSerial || '--'}</span>
                  </div>
                </div>
                <div
                  className="ad-video-area"
                  style={openImage ? { background: `url(${openImage}) center center / cover no-repeat` } : undefined}
                >
                  <div className="ad-video-mask"></div>
                  <div className="ad-video-overlay">
                    <span className="ad-bed-label">床位</span>
                    <span className="ad-bed-number">{openBed || '--'}</span>
                  </div>
                </div>
                <div className="ad-bottom-bar">
                  <span className="ad-bed-id">{openBed ? `${openBed}号床` : '--'}</span>
                  <span className="ad-channel">{`通道 ${openChannel || '--'}`}</span>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default AlertList;
